import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { EmailPreviewDisplay } from './components';
import { WeeklyReport } from '@/types/weeklyReports';

export function WeeklyEmailPreviewTab() {
  const [selectedReport, setSelectedReport] = useState<string | undefined>(undefined);
  const [reports, setReports] = useState<WeeklyReport[]>([]);
  const [renderedEmail, setRenderedEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReports = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data, error } = await supabase
          .from('weekly_reports')
          .select('*')
          .order('week_start', { ascending: false })
          .limit(30);

        if (error) throw error;
        setReports(data as WeeklyReport[]);
      } catch (err: unknown) {
        console.error('Error fetching reports:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch reports');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReports();
  }, []);

  const handleRenderEmail = async (report: WeeklyReport) => {
    if (!report) {
      setError('No report data available');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const { data: emailHtml, error } = await supabase.functions.invoke('preview-insight', {
        body: { report }
      });

      if (error) throw error;
      setRenderedEmail(emailHtml);
    } catch (err: unknown) {
      console.error('Error rendering email:', err);
      setError(err instanceof Error ? err.message : 'Failed to render email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Weekly Email Preview</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-4">
          <Select onValueChange={setSelectedReport}>
            <SelectTrigger className="w-[300px]">
              <SelectValue placeholder="Select a weekly report" />
            </SelectTrigger>
            <SelectContent>
              {reports.map((report) => (
                <SelectItem key={report.id} value={report.id}>
                  {report.week_start}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            onClick={() => {
              const report = reports.find(r => r.id === selectedReport);
              if (report) {
                handleRenderEmail(report);
              } else {
                setError('No report selected');
              }
            }}
            disabled={isLoading || !selectedReport}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Render Email
          </Button>
        </div>
        {error && <p className="text-red-500">{error}</p>}
        {renderedEmail && (
          <EmailPreviewDisplay
            emailType="insight"
            viewMode="preview"
            isLoading={isLoading}
            emailHtml={renderedEmail}
          />
        )}
      </CardContent>
    </Card>
  );
}
